/**
 * buildSceneDescriptor — Build a SceneDescriptor by walking the graph upstream.
 *
 * Pure function: walks GraphIndex upstream from a SceneNode, returns a
 * SceneDescriptor capturing what to build. Builder functions are closures
 * over node params — they read live values at call time.
 *
 * SceneNode calls the builders to realize Three.js objects.
 */

import type { GraphIndex, GraphNode } from '@/runtime/GraphIndex';
import type { ExposedEndpoint } from '@/graph/GraphDefinition';
import { logger } from '@/logging';

// ─────────────────────────────────────────────────────────────
// SceneDescriptor types
// ─────────────────────────────────────────────────────────────

/** A node reference for distributing Three.js objects back to nodes */
export interface NodeRef {
  node: any;
  key: string;
}

/** Transform layer — closure over node params for live reads */
export interface TransformEntry {
  apply: (group: any) => void;
  nodeRef: NodeRef;
}

/** A mesh pipeline: geometry + material + optional transform stack */
export interface MeshEntry {
  buildGeometry: () => any;
  buildMaterial: () => any;
  shadowConfig: { castShadow: boolean; receiveShadow: boolean };
  transforms: TransformEntry[];
  geometryNodeRefs: NodeRef[];
  materialNodeRef: NodeRef;
}

/** A light in the scene */
export interface LightEntry {
  buildLight: () => any;
  nodeRef: NodeRef;
}

/** A polyline mesh (LineSegments with its own material) */
export interface PolylineEntry {
  buildMesh: () => any;
  nodeRef: NodeRef;
}

/** Complete scene description */
export interface SceneDescriptor {
  meshes: MeshEntry[];
  lights: LightEntry[];
  polylines: PolylineEntry[];
  /** Prebuilt Three.js objects (e.g. InstancedMesh from template Repeater) — added directly to scene */
  prebuiltObjects: any[];
  /** Per-frame callbacks from nodes with function params (useExp) */
  animateCallbacks: (() => void)[];
}

// ─────────────────────────────────────────────────────────────
// Type guards for node capabilities
// ─────────────────────────────────────────────────────────────

function hasBuildMaterial(node: any): boolean {
  return typeof node.buildMaterial === 'function';
}

function hasBuildMesh(node: any): boolean {
  return typeof node.buildMesh === 'function';
}

function hasApplyTransform(node: any): boolean {
  return typeof node.applyTransform === 'function';
}

function hasBuildGeometry(node: any): boolean {
  return typeof node.buildGeometry === 'function';
}

function hasBuildLight(node: any): boolean {
  return typeof node.buildLight === 'function';
}

function hasPerformBooleanOp(node: any): boolean {
  return typeof node.performBooleanOp === 'function';
}

function hasMergeFromGeometries(node: any): boolean {
  return typeof node.mergeFromGeometries === 'function';
}

function needsAnimate(node: any): boolean {
  return typeof node.hasFunctionParams === 'function'
    && node.hasFunctionParams();
}

// ─────────────────────────────────────────────────────────────
// Geometry pipeline result (internal)
// ─────────────────────────────────────────────────────────────

interface GeometryPipelineResult {
  buildGeometry: () => any;
  transforms: TransformEntry[];
  geometryNodeRefs: NodeRef[];
}

// ─────────────────────────────────────────────────────────────
// buildSceneDescriptor
// ─────────────────────────────────────────────────────────────

/**
 * Walk upstream from a SceneNode, return a SceneDescriptor.
 */
export function buildSceneDescriptor(
  graphIndex: GraphIndex,
  nodeId: string,
  instancePath: string
): SceneDescriptor {
  const sceneKey = graphIndex.nodeKey(instancePath, nodeId);
  const reverseEdges = graphIndex.getReverseEdges(sceneKey);

  const meshes: MeshEntry[] = [];
  const lights: LightEntry[] = [];
  const polylines: PolylineEntry[] = [];
  const prebuiltObjects: any[] = [];
  const animateCallbacks: (() => void)[] = [];

  for (const edge of reverseEdges) {
    if (edge.targetEndpointId === 'meshIn') {
      collectMeshPipeline(graphIndex, edge.sourceKey, meshes, polylines, prebuiltObjects, animateCallbacks, edge.sourceEndpointId);
    } else if (edge.targetEndpointId === 'lightIn') {
      collectLight(graphIndex, edge.sourceKey, lights, animateCallbacks, edge.sourceEndpointId);
    }
  }

  return { meshes, lights, polylines, prebuiltObjects, animateCallbacks };
}

// ─────────────────────────────────────────────────────────────
// Mesh pipeline collection
// ─────────────────────────────────────────────────────────────

export function collectMeshPipeline(
  graphIndex: GraphIndex,
  sourceKey: string,
  meshes: MeshEntry[],
  polylines: PolylineEntry[],
  prebuiltObjects: any[],
  animateCallbacks: (() => void)[],
  arrivalEndpoint?: string
): void {
  const gn = graphIndex.getNodeByKey(sourceKey);
  if (!gn) {
    // Composite/definition boundary node — not registered in GraphIndex but edges exist.
    // Filter by arrivalEndpoint to follow the correct internal edge.
    const reverseEdges = graphIndex.getReverseEdges(sourceKey);
    const filtered = arrivalEndpoint
      ? reverseEdges.filter(e => e.targetEndpointId === arrivalEndpoint)
      : reverseEdges;
    logger.trace('collectMeshPipeline', `boundary ${sourceKey} arrival=${arrivalEndpoint} reverseEdges=${reverseEdges.length} filtered=${filtered.length}`);
    for (const edge of filtered) {
      logger.trace('collectMeshPipeline', `  → ${edge.sourceKey}.${edge.sourceEndpointId} (target=${edge.targetEndpointId})`);
      collectMeshPipeline(graphIndex, edge.sourceKey, meshes, polylines, prebuiltObjects, animateCallbacks, edge.sourceEndpointId);
    }
    return;
  }
  const node = gn.node as any;

  // Check for prebuilt instanced objects (template Repeater with 3D adapter)
  if (typeof node.getPrebuiltObjects === 'function') {
    const objects = node.getPrebuiltObjects();
    if (objects && objects.length > 0) {
      prebuiltObjects.push(...objects);
      return; // Terminal source — don't walk further upstream
    }
  }

  logger.trace('collectMeshPipeline', `node ${sourceKey} type=${node.type} arrival=${arrivalEndpoint} hasBuildMaterial=${hasBuildMaterial(node)} hasBuildMesh=${hasBuildMesh(node)} hasApplyTransform=${hasApplyTransform(node)}`);

  if (hasBuildMaterial(node)) {
    // MaterialNode: walk upstream for geometry
    const materialNodeRef: NodeRef = { node, key: sourceKey };
    if (needsAnimate(node)) {
      animateCallbacks.push(() => node.animate());
    }
    const reverseEdges = graphIndex.getReverseEdges(sourceKey);
    const geoEdges = reverseEdges.filter(e => e.targetEndpointId === 'geometryIn');

    for (const edge of geoEdges) {
      const result = collectGeometryPipeline(graphIndex, edge.sourceKey, animateCallbacks, edge.sourceEndpointId);
      if (!result) continue;

      meshes.push({
        buildGeometry: result.buildGeometry,
        buildMaterial: () => node.buildMaterial(),
        shadowConfig: typeof node.getShadowConfig === 'function'
          ? node.getShadowConfig()
          : { castShadow: true, receiveShadow: true },
        transforms: result.transforms,
        geometryNodeRefs: result.geometryNodeRefs,
        materialNodeRef
      });
    }
  } else if (hasBuildMesh(node)) {
    // PolylineGeometryNode / MocapNode: builds complete mesh
    if (needsAnimate(node)) {
      animateCallbacks.push(() => node.animate());
    }
    polylines.push({
      buildMesh: () => node.buildMesh(),
      nodeRef: { node, key: sourceKey }
    });
  } else {
    // Non-terminal node — collect any capabilities, then traverse upstream
    let meshTransform: TransformEntry | undefined;
    if (hasApplyTransform(node)) {
      const nodeRef: NodeRef = { node, key: sourceKey };
      meshTransform = { apply: (group: any) => node.applyTransform(group), nodeRef };
      if (needsAnimate(node)) {
        animateCallbacks.push(() => node.animate());
      }
    }

    const reverseEdges = graphIndex.getReverseEdges(sourceKey);
    logger.trace('collectMeshPipeline', `  else-branch reverseEdges=${reverseEdges.length} for ${sourceKey}`);
    const upstreamMeshes: MeshEntry[] = [];
    for (const edge of reverseEdges) {
      const meshEdge = isMeshEdge(node, edge);
      logger.trace('collectMeshPipeline', `  edge ${edge.sourceKey}.${edge.sourceEndpointId} → .${edge.targetEndpointId} isMesh=${meshEdge}`);
      if (!meshEdge) continue;
      collectMeshPipeline(graphIndex, edge.sourceKey, upstreamMeshes, polylines, prebuiltObjects, animateCallbacks, edge.sourceEndpointId);
    }

    // Attach collected capabilities to upstream results
    if (meshTransform) {
      for (const mesh of upstreamMeshes) {
        mesh.transforms = [meshTransform, ...mesh.transforms];
      }
    }
    meshes.push(...upstreamMeshes);
  }
}

// ─────────────────────────────────────────────────────────────
// Geometry pipeline collection
// ─────────────────────────────────────────────────────────────

function collectGeometryPipeline(
  graphIndex: GraphIndex,
  sourceKey: string,
  animateCallbacks: (() => void)[],
  arrivalEndpoint?: string
): GeometryPipelineResult | null {
  const gn = graphIndex.getNodeByKey(sourceKey);
  if (!gn) {
    // Composite/definition boundary node — filter by arrivalEndpoint
    const reverseEdges = graphIndex.getReverseEdges(sourceKey);
    const filtered = arrivalEndpoint
      ? reverseEdges.filter(e => e.targetEndpointId === arrivalEndpoint)
      : reverseEdges;
    if (filtered.length > 0) {
      return collectGeometryPipeline(graphIndex, filtered[0].sourceKey, animateCallbacks, filtered[0].sourceEndpointId);
    }
    return null;
  }
  const node = gn.node as any;


  if (hasApplyTransform(node)) {
    return collectTransformPipeline(graphIndex, gn, node, animateCallbacks);
  } else if (hasPerformBooleanOp(node)) {
    return collectBooleanPipeline(graphIndex, gn, node, animateCallbacks);
  } else if (hasMergeFromGeometries(node)) {
    return collectMergePipeline(graphIndex, gn, node, animateCallbacks);
  } else if (hasBuildGeometry(node)) {
    const nodeRef: NodeRef = { node, key: sourceKey };
    if (needsAnimate(node)) {
      animateCallbacks.push(() => node.animate());
    }
    return {
      buildGeometry: () => node.buildGeometry(),
      transforms: [],
      geometryNodeRefs: [nodeRef]
    };
  }

  // Non-Three.js node — check stored geometry on output endpoint
  const storedGeometry = getStoredOutputGeometry(node);
  if (storedGeometry) {
    return {
      buildGeometry: () => storedGeometry,
      transforms: [],
      geometryNodeRefs: []
    };
  }

  // Subgraph boundary — traverse upstream
  const reverseEdges = graphIndex.getReverseEdges(sourceKey);
  if (reverseEdges.length > 0) {
    return collectGeometryPipeline(graphIndex, reverseEdges[0].sourceKey, animateCallbacks, reverseEdges[0].sourceEndpointId);
  }

  return null;
}

function collectTransformPipeline(
  graphIndex: GraphIndex,
  transformGN: GraphNode,
  transformNode: any,
  animateCallbacks: (() => void)[]
): GeometryPipelineResult | null {
  const nodeRef: NodeRef = { node: transformNode, key: transformGN.key };
  const transformEntry: TransformEntry = {
    apply: (group: any) => transformNode.applyTransform(group),
    nodeRef
  };

  // Capture animate callback if this transform has function params (useExp)
  if (needsAnimate(transformNode)) {
    animateCallbacks.push(() => transformNode.animate());
  }

  const reverseEdges = graphIndex.getReverseEdges(transformGN.key);
  const geoEdges = reverseEdges.filter(e => e.targetEndpointId === 'in');

  if (geoEdges.length === 0) return null;

  // Single upstream: maintain transform chain
  const upstream = collectGeometryPipeline(graphIndex, geoEdges[0].sourceKey, animateCallbacks, geoEdges[0].sourceEndpointId);
  if (!upstream) return null;

  // If multiple geometry inputs to transform, merge them
  if (geoEdges.length > 1) {
    const allResults = [upstream];
    for (let i = 1; i < geoEdges.length; i++) {
      const r = collectGeometryPipeline(graphIndex, geoEdges[i].sourceKey, animateCallbacks, geoEdges[i].sourceEndpointId);
      if (r) allResults.push(r);
    }
    if (allResults.length > 1) {
      const allGeoNodeRefs: NodeRef[] = [];
      const allBuilders: (() => any)[] = [];
      for (const r of allResults) {
        allGeoNodeRefs.push(...r.geometryNodeRefs);
        allBuilders.push(r.buildGeometry);
      }
      return {
        buildGeometry: () => {
          const geos = allBuilders.map(fn => fn()).filter(Boolean);
          return geos.length === 1 ? geos[0] : geos;
        },
        transforms: [transformEntry, ...upstream.transforms],
        geometryNodeRefs: allGeoNodeRefs
      };
    }
  }

  return {
    buildGeometry: upstream.buildGeometry,
    transforms: [transformEntry, ...upstream.transforms],
    geometryNodeRefs: upstream.geometryNodeRefs
  };
}

function collectBooleanPipeline(
  graphIndex: GraphIndex,
  booleanGN: GraphNode,
  booleanNode: any,
  animateCallbacks: (() => void)[]
): GeometryPipelineResult | null {
  const reverseEdges = graphIndex.getReverseEdges(booleanGN.key);

  const edgeA = reverseEdges.find(e => e.targetEndpointId === 'geometryA');
  const edgeB = reverseEdges.find(e => e.targetEndpointId === 'geometryB');

  const resultA = edgeA ? collectGeometryPipeline(graphIndex, edgeA.sourceKey, animateCallbacks, edgeA.sourceEndpointId) : null;
  const resultB = edgeB ? collectGeometryPipeline(graphIndex, edgeB.sourceKey, animateCallbacks, edgeB.sourceEndpointId) : null;

  const geoNodeRefs: NodeRef[] = [{ node: booleanNode, key: booleanGN.key }];
  if (resultA) {
    geoNodeRefs.push(...resultA.geometryNodeRefs);
    for (const t of resultA.transforms) geoNodeRefs.push(t.nodeRef);
  }
  if (resultB) {
    geoNodeRefs.push(...resultB.geometryNodeRefs);
    for (const t of resultB.transforms) geoNodeRefs.push(t.nodeRef);
  }

  return {
    buildGeometry: () => {
      let geoA = resultA?.buildGeometry() ?? null;
      let geoB = resultB?.buildGeometry() ?? null;
      // Bake upstream transforms into geometry vertices for correct CSG
      const three = booleanNode.three;
      if (geoA && resultA?.transforms.length) {
        geoA = bakeTransformsIntoGeometry(geoA, resultA.transforms, three);
      }
      if (geoB && resultB?.transforms.length) {
        geoB = bakeTransformsIntoGeometry(geoB, resultB.transforms, three);
      }
      return booleanNode.performBooleanOp(geoA, geoB);
    },
    transforms: [],
    geometryNodeRefs: geoNodeRefs
  };
}

function collectMergePipeline(
  graphIndex: GraphIndex,
  mergeGN: GraphNode,
  mergeNode: any,
  animateCallbacks: (() => void)[]
): GeometryPipelineResult | null {
  const reverseEdges = graphIndex.getReverseEdges(mergeGN.key);
  const meshEdges = reverseEdges.filter(e => e.targetEndpointId === 'meshIn');

  const upstreamResults: GeometryPipelineResult[] = [];
  for (const edge of meshEdges) {
    const result = collectGeometryPipeline(graphIndex, edge.sourceKey, animateCallbacks, edge.sourceEndpointId);
    if (result) upstreamResults.push(result);
  }

  if (upstreamResults.length === 0) return null;

  const allGeoNodeRefs: NodeRef[] = [{ node: mergeNode, key: mergeGN.key }];
  for (const r of upstreamResults) {
    allGeoNodeRefs.push(...r.geometryNodeRefs);
    for (const t of r.transforms) allGeoNodeRefs.push(t.nodeRef);
  }

  return {
    buildGeometry: () => {
      const geos = upstreamResults.map(r => {
        let geo = r.buildGeometry();
        if (geo && r.transforms.length) {
          geo = bakeTransformsIntoGeometry(geo, r.transforms, mergeNode.three);
        }
        return geo;
      }).filter(Boolean);
      return mergeNode.mergeFromGeometries(geos);
    },
    transforms: [],
    geometryNodeRefs: allGeoNodeRefs
  };
}

// ─────────────────────────────────────────────────────────────
// Light collection
// ─────────────────────────────────────────────────────────────

function collectLight(
  graphIndex: GraphIndex,
  sourceKey: string,
  lights: LightEntry[],
  animateCallbacks: (() => void)[],
  arrivalEndpoint?: string
): void {
  const gn = graphIndex.getNodeByKey(sourceKey);
  if (!gn) {
    // Composite/definition boundary node — filter by arrivalEndpoint
    const reverseEdges = graphIndex.getReverseEdges(sourceKey);
    const filtered = arrivalEndpoint
      ? reverseEdges.filter(e => e.targetEndpointId === arrivalEndpoint)
      : reverseEdges;
    for (const edge of filtered) {
      collectLight(graphIndex, edge.sourceKey, lights, animateCallbacks, edge.sourceEndpointId);
    }
    return;
  }
  const node = gn.node as any;

  if (hasBuildLight(node)) {
    if (needsAnimate(node)) {
      animateCallbacks.push(() => node.animate());
    }
    lights.push({
      buildLight: () => node.buildLight(),
      nodeRef: { node, key: sourceKey }
    });
  } else {
    // Subgraph boundary — traverse upstream
    const reverseEdges = graphIndex.getReverseEdges(sourceKey);
    for (const edge of reverseEdges) {
      if (!isMeshEdge(node, edge)) continue;
      collectLight(graphIndex, edge.sourceKey, lights, animateCallbacks, edge.sourceEndpointId);
    }
  }
}

/**
 * Build per-node SceneDescriptors for performance mode.
 * Returns a Map from nodeId → SceneDescriptor for nodes with mesh/light outputs.
 *
 * When `api` is provided (non-root definitions), only collects from nodes whose
 * outputs are exposed on the definition's API as non-input endpoints.
 * When `api` is omitted (root), discovers all nodes with mesh/light outputs.
 */
export function buildPerNodeDescriptors(
  graphIndex: GraphIndex,
  instancePath: string,
  nodes: Record<string, { endpoints: Record<string, { dataType?: string; isOutput?: boolean }> }>,
  api?: Record<string, ExposedEndpoint>,
): Map<string, SceneDescriptor> {
  const result = new Map<string, SceneDescriptor>();

  // When api is provided, only collect from nodes whose outputs are exposed
  // on the definition's API as non-input endpoints.
  let exposedOutputNodes: Set<string> | null = null;
  if (api) {
    exposedOutputNodes = new Set<string>();
    for (const ep of Object.values(api)) {
      // Skip input-only endpoints — we only want exposed outputs
      if (ep.isInput && !ep.isOutput) continue;
      // path is "nodeId.endpointId"
      const nodeId = ep.path.split('.')[0];
      exposedOutputNodes.add(nodeId);
    }
  }

  // Phase 1: definition-level nodes with mesh/light outputs
  for (const [nodeId, node] of Object.entries(nodes)) {
    // If filtering by API, skip nodes not exposed as outputs
    if (exposedOutputNodes && !exposedOutputNodes.has(nodeId)) continue;

    let hasMesh = false;
    let hasLight = false;

    for (const ep of Object.values(node.endpoints)) {
      if (!ep.isOutput) continue;
      if (ep.dataType?.split('|').includes('three:group')) hasMesh = true;
      // 'any' type may carry mesh data through SwitchNode/subgraph chains
      if (ep.dataType === 'any') hasMesh = true;
      if (ep.dataType?.split('|').includes('three:light')) hasLight = true;
    }

    if (hasMesh || hasLight) {
      logger.trace('buildPerNodeDescriptors', `Phase1 hit: ${nodeId} hasMesh=${hasMesh} hasLight=${hasLight} (endpoints: ${Object.entries(node.endpoints).filter(([,e]) => e.isOutput).map(([id,e]) => `${id}:${e.dataType}`).join(', ')})`);
      const meshes: MeshEntry[] = [];
      const lights: LightEntry[] = [];
      const polylines: PolylineEntry[] = [];
      const prebuiltObjects: any[] = [];
      const animateCallbacks: (() => void)[] = [];
      const nodeKey = graphIndex.nodeKey(instancePath, nodeId);

      if (hasMesh) {
        collectMeshPipeline(graphIndex, nodeKey, meshes, polylines, prebuiltObjects, animateCallbacks);
      }
      if (hasLight) {
        collectLight(graphIndex, nodeKey, lights, animateCallbacks);
      }

      result.set(nodeId, { meshes, lights, polylines, prebuiltObjects, animateCallbacks });
    }
  }

  return result;
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Bake transform stack into geometry vertices (for CSG — transforms must be in vertex space) */
function bakeTransformsIntoGeometry(geo: any, transforms: TransformEntry[], three: any): any {
  const cloned = geo.clone();
  for (let i = transforms.length - 1; i >= 0; i--) {
    const obj = new three.Object3D();
    transforms[i].apply(obj);
    obj.updateMatrix();
    cloned.applyMatrix4(obj.matrix);
  }
  return cloned;
}

/** Check if a reverse edge targets a mesh-compatible endpoint (skip known non-three types) */
function isMeshEdge(node: any, edge: { targetEndpointId: string }): boolean {
  const meta = node.deps?.endpointApi?.getMetadata?.(edge.targetEndpointId);
  if (!meta?.dataType) return true; // no metadata → assume compatible
  const dt = meta.dataType;
  return dt === 'any' || dt.includes('three:');
}

/** Read stored geometry from a non-Three.js node's output endpoint */
function getStoredOutputGeometry(node: any): any {
  const endpointApi = node.deps?.endpointApi;
  if (!endpointApi) return null;

  try {
    const ids = endpointApi.getEndpointIds();
    for (const id of ids) {
      const metadata = endpointApi.getMetadata(id);
      if (metadata?.dataType === 'three:geometry' && metadata?.isOutput) {
        const value = endpointApi.getValue(id);
        if (value) return value;
      }
    }
  } catch {
    // Node may not support endpoint queries
  }
  return null;
}
